/* eslint-disable no-underscore-dangle,max-len,no-param-reassign */
import { i18n } from 'vue-infra';
import { isNull, orderBy, get, pickBy, includes, isEmpty, some, isNil, each, values, cloneDeep } from 'lodash';
import { ROOT_GETTER_PATHS } from '@core/store';
import {
  DATA_LOADING_STATES,
  METRIC_NAMES,
  QUEUE_HEALTH_METRIC_STATUS_ITEMS,
  SORT_DIRECTION,
  WIDGET_LOGICAL_NAMES,
} from '@config/Consts';
import { getLastTimeUpdated, metricAlertData, getDialogAlertItemsByWidget, sendThresholdEvent } from '@core/utils/utils';
import { ALERT_CONFIG } from '@config/AlertConfig';
import AlertModel from '@core/models/AlertModel';

const _lastTimeMetricsUpdated = state =>
  getLastTimeUpdated(state.metrics.lastTimeDataUpdated);

const _lastTimeMetricsTotalUpdated = state =>
  getLastTimeUpdated(state.metricsTotal.lastTimeDataUpdated);

const _lastTimeMetricsOverdueUpdated = state =>
  getLastTimeUpdated(state.metricsOverdue.lastTimeDataUpdated);

const _lastTimeMetricsUpdatedTimestamp = state =>
  getLastTimeUpdated(state.metrics.lastTimeDataUpdated, true);

const _lastTimeMetricsTotalUpdatedTimestamp = state =>
  getLastTimeUpdated(state.metricsTotal.lastTimeDataUpdated, true);

const _lastTimeMetricsOverdueUpdatedTimestamp = state =>
  getLastTimeUpdated(state.metricsOverdue.lastTimeDataUpdated, true);

const _getMetricsDataStatus = state => state.metrics.dataStatus;

const _getMetricsTotalDataStatus = state => state.metricsTotal.dataStatus;

const _getMetricsOverdueDataStatus = state => state.metricsOverdue.dataStatus;

const getMetricAttributes = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES](
    WIDGET_LOGICAL_NAMES.IN_QUEUE);

const getSkills = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_SKILLS];

const getSkillsFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];

const getAlertData = (state, getters, rootState, rootGetters) => {
  const allAlerts = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ALERT_DATA];
  return get(allAlerts, WIDGET_LOGICAL_NAMES.IN_QUEUE) || {};
};

const getTimeFrameFilter = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];

const getFilteredGroupsIds = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];

const getMetricsState = state => state.metrics.value;

const getThresholdEventOptions = (metricName, metricScore, alertData) => ({
  widgetName: WIDGET_LOGICAL_NAMES.IN_QUEUE,
  metricName,
  metricScore,
  alertData,
});

const getMetricAlertData = (metricName, score, validation, data) => {
  const alertData = metricAlertData(data, score, validation);
  const showAlert = alertData.showAlert;
  const tooltip = alertData.tooltip;
  if (showAlert) {
    sendThresholdEvent(getThresholdEventOptions(metricName, score, data));
  }
  return { showAlert, tooltip };
};

const getDialogAlertItemsForSkills = (skillObj) => {
  const alertConfigItems = pickBy(ALERT_CONFIG[WIDGET_LOGICAL_NAMES.IN_QUEUE], (value, key) => includes([METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS, METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME], key));
  const skill = skillObj.name;
  const alertData = skillObj.data;
  const size = isEmpty(alertConfigItems) ? {} : values(alertConfigItems)[0].size;
  const dialogAlertItems = [];
  each(alertConfigItems, (item, metricName) => {
    const alertMetricMap = {
      [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]: `${skill}_unassigned`,
      [METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME]: `${skill}_time`,
    };
    item.metricName = alertMetricMap[metricName];
    item.conditionType = get(alertData, `${item.metricName}.conditionType`);
    item.values = get(alertData, `${item.metricName}.values`);
    dialogAlertItems.push(item);
  });
  return { list: dialogAlertItems, size, widgetName: WIDGET_LOGICAL_NAMES.IN_QUEUE };
};

const getSkillAlertData = (getters, metricData) => {
  const defaultMetricAlertData = { showAlert: false, tooltip: '' };
  let [showSetAlert, isAlertSet, dialogAlertItems, unassignedDataAlert, waitTimeDataAlert] = [false, false, undefined, defaultMetricAlertData, defaultMetricAlertData];
  showSetAlert = metricData.alertPermissions.canSetAlert;
  if (!metricData.alertPermissions.canViewAlert && !metricData.alertPermissions.canSetAlert) {
    return { showSetAlert, isAlertSet, dialogAlertItems, unassignedDataAlert, waitTimeDataAlert };
  }
  const skill = metricData.skillName;
  const alertConfigItems = pickBy(ALERT_CONFIG[WIDGET_LOGICAL_NAMES.IN_QUEUE], (value, key) => includes([METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS, METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME], key));
  const allMetrics = [`${skill}_unassigned`, `${skill}_time`];
  const allMetricAlerts = pickBy(getters.getAlertData, (value, key) => includes(allMetrics, key));
  dialogAlertItems = cloneDeep(getDialogAlertItemsForSkills({ name: skill, data: allMetricAlerts }));
  if (!isEmpty(allMetricAlerts)) {
    isAlertSet = some(allMetricAlerts, metricAlert => !isEmpty(get(metricAlert, 'conditionType')));
    if (isAlertSet && metricData.alertPermissions.canViewAlert) {
      unassignedDataAlert = getMetricAlertData(`${skill}_unassigned`, metricData.unassigned, alertConfigItems[METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS].validation, get(allMetricAlerts, `${skill}_unassigned`));
      waitTimeDataAlert = getMetricAlertData(`${skill}_time`, metricData.waitTime, alertConfigItems[METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME].validation, get(allMetricAlerts, `${skill}_time`));
      unassignedDataAlert.alertModel = unassignedDataAlert.showAlert && new AlertModel({
        origin: `${WIDGET_LOGICAL_NAMES.IN_QUEUE}_${skill}_unassigned`,
        message: `${i18n.t('amd:metric_skill')} ${skill} ${i18n.t('amd:alerts_configuration_queue_summary_in_queue')} ${unassignedDataAlert.tooltip}`,
        showAlert: unassignedDataAlert.showAlert,
      });
      waitTimeDataAlert.alertModel = waitTimeDataAlert.showAlert && new AlertModel({
        origin: `${WIDGET_LOGICAL_NAMES.IN_QUEUE}_${skill}_time`,
        message: `${i18n.t('amd:metric_skill')} ${skill} ${i18n.t('amd:alerts_configuration_queue_summary_wait_time')} ${waitTimeDataAlert.tooltip}`,
        showAlert: waitTimeDataAlert.showAlert,
      });
    }
  }
  return { showSetAlert, isAlertSet, dialogAlertItems, unassignedDataAlert, waitTimeDataAlert };
};

const getMetrics = (state, getters, rootState, rootGetters) => {
  const metrics = [...getMetricsState(state) || []];
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  const result = [];
  each(metrics, (metric) => {
    const skillName = metric[METRIC_NAMES.IN_QUEUE_SKILL_NAME];
    const unassigned = metric[METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS];
    const waitTime = metric[METRIC_NAMES.IN_QUEUE_SKILL_WAIT_TIME];
    const skillAlertData = getSkillAlertData(getters, { skillName, unassigned, waitTime, alertPermissions });
    result.push({ ...metric, ...skillAlertData });
  });
  return result;
};

const getMetricsTotal = state => state.metricsTotal.value;

const getMetricsOverdue = state => state.metricsOverdue.value;

const getSort = state => state.sort;

const isMetricsDataOutOfDate = state =>
  _getMetricsDataStatus(state) === DATA_LOADING_STATES.WRONG_FORMAT
|| (_getMetricsDataStatus(state) === DATA_LOADING_STATES.ERROR
  && !isNull(getMetricsState(state)));

const isMetricsTotalDataOutOfDate = state =>
  _getMetricsTotalDataStatus(state) === DATA_LOADING_STATES.WRONG_FORMAT
  || (_getMetricsTotalDataStatus(state) === DATA_LOADING_STATES.ERROR
  && !isNull(getMetricsTotal(state)));

const isMetricsOverdueDataOutOfDate = state =>
  _getMetricsOverdueDataStatus(state) === DATA_LOADING_STATES.WRONG_FORMAT
  || (_getMetricsOverdueDataStatus(state) === DATA_LOADING_STATES.ERROR
  && !isNull(getMetricsOverdue(state)));

const allDataHasBeenFetched = state =>
  !isNull(getMetricsState(state)) &&
  !isNull(getMetricsTotal(state)) &&
  !isNull(getMetricsOverdue(state));

/**
 * @return {boolean}
 */
const isWaitingForData = (state, getters, rootState, rootGetters) => {
  const loadingState = DATA_LOADING_STATES.LOADING;
  return rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === loadingState
    || (
      _getMetricsDataStatus(state) === loadingState
      && _getMetricsTotalDataStatus(state) === loadingState
      && _getMetricsOverdueDataStatus(state) === loadingState
    );
};

const _allMetricsAreInErrorState = (state) => {
  const errorState = DATA_LOADING_STATES.ERROR;
  return _getMetricsDataStatus(state) === errorState
  && _getMetricsTotalDataStatus(state) === errorState
  && _getMetricsOverdueDataStatus(state) === errorState;
};

/* The widget loading error is displayed in the following cases:
  - one of the MWS prerequesites api is down
  - the loading states of the 3 QH metrics are in the error state
*/
const displayWidgetError = (state, getters, rootState, rootGetters) => {
  const errorState = DATA_LOADING_STATES.ERROR;
  const result = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === errorState
  || (
    !allDataHasBeenFetched(state) && _allMetricsAreInErrorState(state)
  );
  return result;
};

/*
data is considered out of dated when one or more metrics
are out of dated or are in error state when not first time load.
The out of date error ui is currently a wrapping red border on the widget with a related tooltip
 */
const showOutOfDateError = state =>
  isMetricsTotalDataOutOfDate(state)
    || isMetricsDataOutOfDate(state)
    || isMetricsOverdueDataOutOfDate(state)
    || (allDataHasBeenFetched(state) && _allMetricsAreInErrorState(state));

const getOutOfDateData = (state) => {
  let metrics;
  const dataOutOfDateList = [];
  // TODO: we need to make this generic. To do so we will need to replace
  // all the specific getters with generic one like we will use in the AC error handling
  if (isMetricsDataOutOfDate(state)) {
    dataOutOfDateList.push({
      lastUpdate: _lastTimeMetricsUpdatedTimestamp(state),
      lastUpdateText: _lastTimeMetricsUpdated(state),
      metric: QUEUE_HEALTH_METRIC_STATUS_ITEMS.SKILLS_LIST,
      isFetched: !isNull(getMetricsState(state)),
    });
  }
  if (isMetricsTotalDataOutOfDate(state)) {
    dataOutOfDateList.push({
      lastUpdate: _lastTimeMetricsTotalUpdatedTimestamp(state),
      lastUpdateText: _lastTimeMetricsTotalUpdated(state),
      metric: QUEUE_HEALTH_METRIC_STATUS_ITEMS.IN_QUEUE_TOTAL,
      isFetched: !isNull(getMetricsTotal(state)),
    });
  }
  if (isMetricsOverdueDataOutOfDate(state)) {
    dataOutOfDateList.push({
      lastUpdate: _lastTimeMetricsOverdueUpdatedTimestamp(state),
      lastUpdateText: _lastTimeMetricsOverdueUpdated(state),
      metric: QUEUE_HEALTH_METRIC_STATUS_ITEMS.OVERDUE_TOTAL,
      isFetched: !isNull(getMetricsOverdue(state)),
    });
  }
  if (dataOutOfDateList.length > 1) {
    // if the metrics have different timestamp, take the latest update time between all
    metrics = orderBy(dataOutOfDateList, ['lastUpdate'], ['asc']);
  } else {
    metrics = dataOutOfDateList;
  }
  return metrics;
};

const getPresetValues = (state, getters) => ({
  sort: getters.getSort || {
    columnName: METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  },
});

const getDialogAlertItems = (getters, metrics) =>
  getDialogAlertItemsByWidget(metrics, WIDGET_LOGICAL_NAMES.IN_QUEUE, getters.getAlertData);

const getDialogAlertItemsForTotalAndOverdue = (state, getters) => {
  const metrics = [METRIC_NAMES.IN_QUEUE_UNASSIGNED_TOTAL, METRIC_NAMES.OVERDUE_IN_QUEUE];
  const metricNameTranslated = i18n.t('queue_health_total_in_queue_name');
  return getDialogAlertItems(getters, metrics, metricNameTranslated);
};

const showSetAlertForTotalAndOverdue = (state, getters, rootState, rootGetters) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  if (!alertPermissions.canSetAlert) {
    return false;
  }
  const metricAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.IN_QUEUE}.${METRIC_NAMES.IN_QUEUE_UNASSIGNED_TOTAL}`);
  return !!get(metricAlertConfig, 'showAlertConfiguration');
};

const getAllMetricAlertsForTotalAndOverdue = (state, getters) => {
  const metrics = [METRIC_NAMES.IN_QUEUE_UNASSIGNED_TOTAL, METRIC_NAMES.OVERDUE_IN_QUEUE];
  return pickBy(getters.getAlertData, (value, key) => includes(metrics, key));
};

const isAlertSetForTotalOrOverdue = (state, getters, rootState, rootGetters) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  if (!alertPermissions.canViewAlert) {
    return false;
  }
  const allMetricAlerts = getAllMetricAlertsForTotalAndOverdue(state, getters);
  return some(allMetricAlerts, metricAlert => !isEmpty(get(metricAlert, 'conditionType')));
};

const getMetricShowAlert = (state, getters, rootGetters, score, metricName) => {
  const alertPermissions = rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_HAS_ALERT_PERMISSIONS];
  if (!alertPermissions.canViewAlert) {
    return { showAlert: false, tooltip: '', alertModel: null };
  }
  let showAlert = false;
  let tooltip = '';
  let alertHistoryModel;
  const metricScore = isNil(score) ? null : score;
  const allMetricAlerts = getAllMetricAlertsForTotalAndOverdue(state, getters);
  if (getters.isAlertSetForTotalOrOverdue) {
    const metricAlertConfig = get(ALERT_CONFIG, `${WIDGET_LOGICAL_NAMES.IN_QUEUE}.${metricName}`);
    const name = i18n.t(`amd:${metricAlertConfig.nameI18n}`);
    const currentMetricAlert = get(allMetricAlerts, metricName);
    if (currentMetricAlert) {
      const metricAlert = metricAlertData(get(allMetricAlerts, metricName), metricScore, metricAlertConfig.validation, name);
      showAlert = metricAlert.showAlert;
      tooltip = metricAlert.tooltip;
      if (showAlert) {
        sendThresholdEvent(getThresholdEventOptions(metricName, metricScore, get(getters.getAlertData, metricName)));
        alertHistoryModel = new AlertModel({
          origin: `${WIDGET_LOGICAL_NAMES.IN_QUEUE}_${metricName}`,
          message: tooltip,
          showAlert: true });
      } else {
        alertHistoryModel = new AlertModel({
          origin: `${WIDGET_LOGICAL_NAMES.IN_QUEUE}_${metricName}`,
          message: tooltip,
          showAlert: false });
      }
    }
  }
  return { showAlert, tooltip, alertModel: alertHistoryModel };
};

const showAlertForTotal = (state, getters, rootState, rootGetters) => getMetricShowAlert(state, getters, rootGetters, getMetricsTotal(state), METRIC_NAMES.IN_QUEUE_UNASSIGNED_TOTAL);

const showAlertForOverdue = (state, getters, rootState, rootGetters) => getMetricShowAlert(state, getters, rootGetters, getMetricsOverdue(state), METRIC_NAMES.OVERDUE_IN_QUEUE);

export default {
  getSkills,
  getSkillsFilter,
  getMetricsState,
  getMetrics,
  getMetricsTotal,
  getMetricsOverdue,
  getSort,
  isWaitingForData,
  displayWidgetError,
  showOutOfDateError,
  isMetricsTotalDataOutOfDate,
  isMetricsOverdueDataOutOfDate,
  isMetricsDataOutOfDate,
  getOutOfDateData,
  getMetricAttributes,
  getPresetValues,
  getAlertData,
  getDialogAlertItemsForTotalAndOverdue,
  showSetAlertForTotalAndOverdue,
  isAlertSetForTotalOrOverdue,
  showAlertForTotal,
  showAlertForOverdue,
  getTimeFrameFilter,
  getFilteredGroupsIds,
};
